import { useCallback, useEffect, useRef, useState } from "react";
import { triggerBrowserDownload } from "@/lib/api";

const FAILURE_NOTICE_MS = 3000;

interface DownloadRequest {
  filename: string;
  resolveUrl: () => Promise<string | null | undefined>;
}

export function useDownloadAsset() {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const download = useCallback(async ({ filename, resolveUrl }: DownloadRequest) => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setDownloading(true);
    setFailed(false);
    try {
      const url = await resolveUrl();
      if (!url) throw new Error("no download url");
      triggerBrowserDownload(url, filename);
    } catch {
      if (!mountedRef.current) return;
      setFailed(true);
      timerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setFailed(false);
      }, FAILURE_NOTICE_MS);
    } finally {
      if (mountedRef.current) setDownloading(false);
    }
  }, []);

  const reset = useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setFailed(false);
  }, []);

  return { download, downloading, failed, reset };
}
